import { FunctionComponent, useEffect, useState } from "react";
import { connect } from "react-redux";
import { Dispatch, RootState } from "core/store/store.types";
import {
  Box,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  practiceActions,
  practiceAsync,
  practiceSelectors,
} from "core/store/modules/practice";
import { Practice } from "core/types/practice.types";
import { Text } from "components/atoms/text";
import PracticeListTable from "containers/practice-list-table/practice-list-table";
import { reportAsync, reportSelectors } from "core/store/modules/report";
import { User } from "core/types/user.types";
import { authAsync, authSelectors } from "core/store/modules/auth";
import { Result } from "core/types/search.types";
import { searchAsync, searchSelectors } from "core/store/modules/search";

interface OwnProps {}
interface StateProps {
  user?: User;
  isPracticesLoading: boolean;
  practicesList?: Practice[];
}
interface DispatchProps {
  getUserDetail: () => void;
  getPracticesList: () => void;
  getAdminPracticesList: () => void;
}

export type Props = OwnProps & StateProps & DispatchProps;

const Practices: FunctionComponent<Props> = ({
  user,
  isPracticesLoading,
  practicesList,
  getUserDetail,
  getAdminPracticesList,
  getPracticesList,
}) => {
  const { t } = useTranslation();
  const [orderedList, setOrderedList] = useState<Practice[]>();
  const [order, setOrder] = useState("DESC");

  useEffect(() => {
    getUserDetail();
  }, [getUserDetail]);

  useEffect(() => {
    if (user?.role === "admin" || user?.role === "legals") {
      getAdminPracticesList();
    } else {
      getPracticesList();
    }
  }, [user]);

  useEffect(() => {
    var orderedPractices: Practice[] | undefined = [];
    if (practicesList) {
      if (order === "DESC") {
        orderedPractices = practicesList.sort(
          (a, b) =>
            new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
        );
      } else if (order === "ASC") {
        orderedPractices = practicesList.sort(
          (a, b) =>
            new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime()
        );
      }
    }
    setOrderedList(orderedPractices ? [...orderedPractices] : practicesList);
  }, [practicesList, order]);

  return (
    <>
      <Paper
        sx={{
          borderRadius: "10px",
          padding: "20px",
        }}
      >
        <Box
          paddingY={"15px"}
          display={"flex"}
          justifyContent={"space-between"}
          alignItems={"center"}
        >
          <Text fontSize={"16px"} fontWeight={"700"}>
            {t("practices.title")}
          </Text>
          <FormControl variant="standard" sx={{ width: "30%" }}>
            <InputLabel
              id="select-label"
              sx={{ fontSize: "14px !important", fontWeight: "700" }}
            >
              {t("practices.order.label")}
            </InputLabel>
            <Select
              labelId="demo-simple-select-label"
              id="demo-simple-select"
              value={order}
              label="Age"
              onChange={(event: SelectChangeEvent) =>
                setOrder(event.target.value as string)
              }
            >
              <MenuItem value={"DESC"}>{t("practices.order.DESC")}</MenuItem>
              <MenuItem value={"ASC"}>{t("practices.order.ASC")}</MenuItem>
            </Select>
          </FormControl>
          {/* <Button variant="contained" onClick={() => setOpen(true)}>
            {t("practices.button")}
          </Button> */}
        </Box>
        {orderedList && (
          <Grid container spacing={6}>
            <Grid item xs={12} display={"inline-block"} maxWidth={"100%"}>
              <PracticeListTable
                isPracticeLoading={isPracticesLoading}
                practicesList={orderedList}
              />
            </Grid>
          </Grid>
        )}
      </Paper>
    </>
  );
};

export default connect<StateProps, DispatchProps, OwnProps, RootState>(
  (state: RootState) => ({
    user: authSelectors.getUser(state),
    isPracticesLoading: practiceSelectors.isPracticeLoading(state),
    practicesList: practiceSelectors.getPracticesList(state),
  }),
  (dispatch: Dispatch) => ({
    getUserDetail: () => dispatch(authAsync.getUser()),
    getPracticesList: () => dispatch(practiceAsync.getPractices()),
    getAdminPracticesList: () => dispatch(practiceAsync.getAdminPractices()),
  })
)(Practices);
